/**
 * RedactionCanvas — TypeScript port of RedactionCanvasView.kt.
 *
 * Behavior parity:
 *   - 1-pointer drag pans by default
 *   - "+" FAB arms ONE drag for a new rectangle, then dis-arms
 *   - double-tap → fit-view
 *   - 2-pointer pinch zooms (touch + trackpad)
 *   - tap an existing rect to select it
 *   - drag corner / edge handles to resize
 *   - tap empty space to deselect
 */

import {
  applyFillForMode,
  red,
  green,
  blue,
  shuffleTile,
  type RedactionRect,
} from "./renderer.js";
import type { BgMode } from "./settings.js";
import type { OcrTokenBox } from "./ocr.js";

interface Point { x: number; y: number; }

type Handle = "NONE" | "TL" | "TR" | "BL" | "BR" | "T" | "B" | "L" | "R";
type Gesture = "NONE" | "DRAW_NEW" | "MOVE" | "RESIZE" | "PAN";

const HANDLE_ORDER: Handle[] = ["TL", "TR", "BL", "BR", "T", "B", "L", "R"];

export interface CanvasCallbacks {
  onSelectionChanged?: (selected: boolean) => void;
  onArmStateChanged?: (armed: boolean) => void;
}

export class RedactionCanvas {
  private bitmap: HTMLImageElement | ImageBitmap | HTMLCanvasElement | null = null;
  private bitmapW = 0;
  private bitmapH = 0;
  private bitmapCanvas: HTMLCanvasElement | null = null;
  private bitmapImageData: ImageData | null = null;

  private rects: RedactionRect[] = [];
  private shuffleCache = new Map<number, ImageData>();
  private selectedIndex = -1;

  private bgMode: BgMode = "DETECT";
  private bgColor = 0xff000000;
  private previewAlpha = 153;

  private debugTokens: OcrTokenBox[] = [];
  private debugText = "";
  private debugEnabled = false;

  // Affine transform — bitmap pixels → CSS pixels on the canvas element.
  private scale = 1;
  private tx = 0;
  private ty = 0;

  private gesture: Gesture = "NONE";
  private grabHandle: Handle = "NONE";
  private grabIndex = -1;
  private dragStart: Point | null = null;
  private dragCur: Point | null = null;
  private rectAtGrab: RedactionRect["rect"] | null = null;
  private lastPan: Point | null = null;
  private armed = false;

  private pointers = new Map<number, Point>();
  private pinchStart: { dist: number; mid: Point; scale: number; tx: number; ty: number } | null = null;

  private lastTap = 0;

  private readonly minScale = 0.05;
  private readonly maxScale = 12;
  private readonly tapSlop = 8;
  private readonly handleSlop = 14;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly cb: CanvasCallbacks = {},
  ) {
    this.installPointerHandlers();
    this.installResizeObserver();
  }

  /* ---------------------------------------------------------------------- */
  /* Public API                                                             */
  /* ---------------------------------------------------------------------- */

  setImage(bmp: HTMLImageElement | ImageBitmap | HTMLCanvasElement, initial: RedactionRect[] = []): void {
    this.bitmap = bmp;
    this.bitmapW = "width" in bmp ? bmp.width : 0;
    this.bitmapH = "height" in bmp ? bmp.height : 0;

    const off = document.createElement("canvas");
    off.width = this.bitmapW;
    off.height = this.bitmapH;
    const ctx = off.getContext("2d")!;
    ctx.drawImage(bmp as CanvasImageSource, 0, 0);
    this.bitmapCanvas = off;
    this.bitmapImageData = ctx.getImageData(0, 0, this.bitmapW, this.bitmapH);

    this.rects = initial.map((r) => this.fillForNewRect(r));
    this.shuffleCache.clear();
    this.setSelectedIndex(-1);
    this.fitView();
    this.draw();
  }

  setDebugOcr(tokens: readonly OcrTokenBox[], text: string, enabled: boolean): void {
    this.debugTokens = [...tokens];
    this.debugText = text;
    this.debugEnabled = enabled;
    this.draw();
  }

  applySettings(bgMode: BgMode, bgColor: number, previewAlpha: number): void {
    const fillStyleChanged =
      this.bgMode !== bgMode ||
      (bgMode === "COLOR" && this.bgColor !== bgColor);
    this.bgMode = bgMode;
    this.bgColor = bgColor;
    this.previewAlpha = previewAlpha;
    if (fillStyleChanged) this.recomputeAllFills();
    this.draw();
  }

  recomputeAllFills(): void {
    if (!this.bitmapImageData) return;
    this.rects = this.rects.map((r) =>
      applyFillForMode(r, this.bgMode, this.bgColor, this.bitmapImageData!),
    );
    this.shuffleCache.clear();
    this.draw();
  }

  replaceAutoRects(newAuto: RedactionRect[]): void {
    const manuals = this.rects.filter((r) => r.source === "MANUAL");
    this.rects = [
      ...newAuto.map((r) => this.fillForNewRect(r)),
      ...manuals,
    ];
    this.shuffleCache.clear();
    this.setSelectedIndex(-1);
    this.draw();
  }

  currentRects(): readonly RedactionRect[] {
    return this.rects;
  }

  imageBitmapSource(): { canvas: HTMLCanvasElement; width: number; height: number } | null {
    if (!this.bitmapCanvas) return null;
    return { canvas: this.bitmapCanvas, width: this.bitmapW, height: this.bitmapH };
  }

  armNextDrag(): void {
    if (!this.armed) {
      this.armed = true;
      this.cb.onArmStateChanged?.(true);
      this.draw();
    }
  }

  cancelArm(): void {
    if (this.armed) {
      this.armed = false;
      this.cb.onArmStateChanged?.(false);
      this.draw();
    }
  }

  isArmed(): boolean {
    return this.armed;
  }

  hasSelection(): boolean {
    return this.selectedIndex >= 0 && this.selectedIndex < this.rects.length;
  }

  deleteSelected(): void {
    if (!this.hasSelection()) return;
    this.shuffleCache.delete(this.selectedIndex);
    this.rects.splice(this.selectedIndex, 1);
    this.setSelectedIndex(-1);
    this.draw();
  }

  fitView(): void {
    if (!this.bitmapW || !this.bitmapH) return;
    const w = this.canvas.width;
    const h = this.canvas.height;
    if (w === 0 || h === 0) return;
    const s = Math.min(w / this.bitmapW, h / this.bitmapH);
    this.scale = s;
    this.tx = (w - this.bitmapW * s) / 2;
    this.ty = (h - this.bitmapH * s) / 2;
    this.draw();
  }

  /* ---------------------------------------------------------------------- */
  /* Internals                                                              */
  /* ---------------------------------------------------------------------- */

  private setSelectedIndex(i: number): void {
    const before = this.selectedIndex >= 0;
    this.selectedIndex = i;
    const after = i >= 0;
    if (before !== after) this.cb.onSelectionChanged?.(after);
  }

  private fillForNewRect(r: RedactionRect): RedactionRect {
    if (!this.bitmapImageData) return r;
    if (r.fillMode === "SHUFFLE") return r;
    if (r.fillColor !== 0xff000000 && r.fillColor !== 0) return r;
    return applyFillForMode(r, this.bgMode, this.bgColor, this.bitmapImageData);
  }

  private installResizeObserver(): void {
    const sync = () => this.syncCanvasSize();
    sync();
    new ResizeObserver(sync).observe(this.canvas);
    window.addEventListener("resize", sync);
  }

  private syncCanvasSize(): void {
    const dpr = window.devicePixelRatio || 1;
    const rect = this.canvas.getBoundingClientRect();
    const w = Math.max(1, Math.round(rect.width * dpr));
    const h = Math.max(1, Math.round(rect.height * dpr));
    if (this.canvas.width !== w || this.canvas.height !== h) {
      this.canvas.width = w;
      this.canvas.height = h;
      if (this.bitmap) this.fitView();
    }
    this.draw();
  }

  private installPointerHandlers(): void {
    const c = this.canvas;
    c.addEventListener("pointerdown", (e) => this.onPointerDown(e));
    c.addEventListener("pointermove", (e) => this.onPointerMove(e));
    c.addEventListener("pointerup", (e) => this.onPointerUp(e));
    c.addEventListener("pointercancel", (e) => this.onPointerUp(e));
    c.addEventListener("wheel", (e) => this.onWheel(e), { passive: false });
  }

  /** Convert a pointer event's clientX/Y to canvas-bitmap-pixel coordinates. */
  private toCanvasPx(e: PointerEvent): Point {
    const dpr = window.devicePixelRatio || 1;
    const rect = this.canvas.getBoundingClientRect();
    return {
      x: (e.clientX - rect.left) * dpr,
      y: (e.clientY - rect.top) * dpr,
    };
  }

  private viewToImage(p: Point): Point {
    return { x: (p.x - this.tx) / this.scale, y: (p.y - this.ty) / this.scale };
  }

  private imageToView(p: Point): Point {
    return { x: p.x * this.scale + this.tx, y: p.y * this.scale + this.ty };
  }

  private onPointerDown(e: PointerEvent): void {
    this.canvas.setPointerCapture(e.pointerId);
    const p = this.toCanvasPx(e);
    this.pointers.set(e.pointerId, p);

    if (this.pointers.size === 2) {
      this.startPinch();
      this.cancelEditGesture();
      this.draw();
      return;
    }

    // Double-tap detection (~280 ms).
    const now = performance.now();
    if (now - this.lastTap < 280) {
      this.fitView();
      this.lastTap = 0;
      return;
    }
    this.lastTap = now;

    this.dragStart = p;
    this.dragCur = p;
    this.lastPan = p;

    // 1. Resize handle on currently-selected rect?
    if (this.hasSelection()) {
      const mapped = this.mappedRectView(this.rects[this.selectedIndex].rect);
      const h = this.handleAt(p, mapped);
      if (h !== "NONE") {
        this.gesture = "RESIZE";
        this.grabHandle = h;
        this.grabIndex = this.selectedIndex;
        this.rectAtGrab = { ...this.rects[this.selectedIndex].rect };
        return;
      }
    }

    // 2. Hit an existing rect?
    const ip = this.viewToImage(p);
    let hitIdx = -1;
    for (let i = this.rects.length - 1; i >= 0; i--) {
      const r = this.rects[i].rect;
      if (ip.x >= r.left && ip.x <= r.right && ip.y >= r.top && ip.y <= r.bottom) {
        hitIdx = i;
        break;
      }
    }
    if (hitIdx >= 0) {
      this.setSelectedIndex(hitIdx);
      this.grabIndex = hitIdx;
      this.gesture = "MOVE";
      this.rectAtGrab = { ...this.rects[hitIdx].rect };
      this.draw();
      return;
    }

    // 3. Empty canvas — armed → DRAW_NEW; else PAN.
    this.setSelectedIndex(-1);
    this.gesture = this.armed ? "DRAW_NEW" : "PAN";
    this.draw();
  }

  private onPointerMove(e: PointerEvent): void {
    if (!this.pointers.has(e.pointerId)) return;
    const p = this.toCanvasPx(e);
    this.pointers.set(e.pointerId, p);

    if (this.pointers.size >= 2 && this.pinchStart) {
      this.applyPinch();
      this.draw();
      return;
    }

    this.dragCur = p;
    switch (this.gesture) {
      case "MOVE": this.applyMove(); break;
      case "RESIZE": this.applyResize(); break;
      case "PAN": {
        if (this.lastPan) {
          this.tx += p.x - this.lastPan.x;
          this.ty += p.y - this.lastPan.y;
        }
        this.lastPan = p;
        break;
      }
      default:
        // DRAW_NEW updates the rubber-band rectangle in draw().
        break;
    }
    this.draw();
  }

  private onPointerUp(e: PointerEvent): void {
    this.pointers.delete(e.pointerId);
    if (this.pointers.size < 2) this.pinchStart = null;
    if (this.pointers.size === 0) this.handleUp();
    this.draw();
  }

  private handleUp(): void {
    const s = this.dragStart;
    const c = this.dragCur;
    this.dragStart = null;
    this.dragCur = null;
    this.lastPan = null;
    if (!s || !c) {
      this.gesture = "NONE";
      return;
    }
    const moved = Math.abs(c.x - s.x) + Math.abs(c.y - s.y) >= this.tapSlop;
    if (this.gesture === "DRAW_NEW") {
      if (moved) this.commitNewRect(s, c);
      if (this.armed) {
        this.armed = false;
        this.cb.onArmStateChanged?.(false);
      }
    }
    this.gesture = "NONE";
    this.grabHandle = "NONE";
    this.grabIndex = -1;
    this.rectAtGrab = null;
  }

  private startPinch(): void {
    const pts = [...this.pointers.values()];
    if (pts.length < 2) return;
    const a = pts[0]; const b = pts[1];
    const dist = Math.hypot(a.x - b.x, a.y - b.y);
    this.pinchStart = {
      dist,
      mid: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 },
      scale: this.scale,
      tx: this.tx,
      ty: this.ty,
    };
  }

  private applyPinch(): void {
    if (!this.pinchStart) return;
    const pts = [...this.pointers.values()];
    if (pts.length < 2) return;
    const a = pts[0]; const b = pts[1];
    const dist = Math.hypot(a.x - b.x, a.y - b.y);
    const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
    let factor = dist / Math.max(1, this.pinchStart.dist);
    let proposed = this.pinchStart.scale * factor;
    if (proposed < this.minScale) { proposed = this.minScale; factor = proposed / this.pinchStart.scale; }
    if (proposed > this.maxScale) { proposed = this.maxScale; factor = proposed / this.pinchStart.scale; }
    // Anchor zoom around the gesture's mid-point at gesture start.
    const m = this.pinchStart.mid;
    this.scale = proposed;
    this.tx = m.x - (m.x - this.pinchStart.tx) * factor + (mid.x - m.x);
    this.ty = m.y - (m.y - this.pinchStart.ty) * factor + (mid.y - m.y);
  }

  private onWheel(e: WheelEvent): void {
    e.preventDefault();
    const dpr = window.devicePixelRatio || 1;
    const rect = this.canvas.getBoundingClientRect();
    const cx = (e.clientX - rect.left) * dpr;
    const cy = (e.clientY - rect.top) * dpr;
    let factor = e.deltaY < 0 ? 1.12 : 1 / 1.12;
    let proposed = this.scale * factor;
    if (proposed < this.minScale) { proposed = this.minScale; factor = proposed / this.scale; }
    if (proposed > this.maxScale) { proposed = this.maxScale; factor = proposed / this.scale; }
    this.tx = cx - (cx - this.tx) * factor;
    this.ty = cy - (cy - this.ty) * factor;
    this.scale = proposed;
    this.draw();
  }

  private cancelEditGesture(): void {
    this.gesture = "NONE";
    this.grabHandle = "NONE";
    this.grabIndex = -1;
    this.rectAtGrab = null;
    this.dragStart = null;
    this.dragCur = null;
    this.lastPan = null;
  }

  private commitNewRect(s: Point, c: Point): void {
    if (!this.bitmapW || !this.bitmapH) return;
    const a = this.viewToImage(s);
    const b = this.viewToImage(c);
    const left = Math.max(0, Math.floor(Math.min(a.x, b.x)));
    const top = Math.max(0, Math.floor(Math.min(a.y, b.y)));
    const right = Math.min(this.bitmapW, Math.ceil(Math.max(a.x, b.x)));
    const bottom = Math.min(this.bitmapH, Math.ceil(Math.max(a.y, b.y)));
    if (right - left < 4 || bottom - top < 4) return;
    const draft: RedactionRect = {
      rect: { left, top, right, bottom },
      source: "MANUAL",
      fillMode: "SOLID",
      fillColor: 0xff000000,
    };
    this.rects.push(this.fillForNewRect(draft));
    this.setSelectedIndex(this.rects.length - 1);
  }

  private applyMove(): void {
    if (!this.rectAtGrab || !this.dragStart || !this.dragCur) return;
    if (this.grabIndex < 0 || this.grabIndex >= this.rects.length) return;
    const a = this.viewToImage(this.dragStart);
    const b = this.viewToImage(this.dragCur);
    const dx = Math.round(b.x - a.x);
    const dy = Math.round(b.y - a.y);
    let { left, top, right, bottom } = this.rectAtGrab;
    left += dx; right += dx; top += dy; bottom += dy;
    const w = right - left; const h = bottom - top;
    if (left < 0) { left = 0; right = w; }
    if (top < 0) { top = 0; bottom = h; }
    if (right > this.bitmapW) { right = this.bitmapW; left = this.bitmapW - w; }
    if (bottom > this.bitmapH) { bottom = this.bitmapH; top = this.bitmapH - h; }
    const cur = this.rects[this.grabIndex];
    this.rects[this.grabIndex] = { ...cur, rect: { left, top, right, bottom } };
    this.shuffleCache.delete(this.grabIndex);
  }

  private applyResize(): void {
    if (!this.rectAtGrab || !this.dragStart || !this.dragCur) return;
    if (this.grabIndex < 0 || this.grabIndex >= this.rects.length) return;
    const a = this.viewToImage(this.dragStart);
    const b = this.viewToImage(this.dragCur);
    const dx = Math.round(b.x - a.x);
    const dy = Math.round(b.y - a.y);
    let { left, top, right, bottom } = this.rectAtGrab;
    switch (this.grabHandle) {
      case "TL": left += dx; top += dy; break;
      case "TR": right += dx; top += dy; break;
      case "BL": left += dx; bottom += dy; break;
      case "BR": right += dx; bottom += dy; break;
      case "T": top += dy; break;
      case "B": bottom += dy; break;
      case "L": left += dx; break;
      case "R": right += dx; break;
      default: return;
    }
    if (right - left < 8) {
      if (this.grabHandle === "L" || this.grabHandle === "TL" || this.grabHandle === "BL") left = right - 8;
      else right = left + 8;
    }
    if (bottom - top < 8) {
      if (this.grabHandle === "T" || this.grabHandle === "TL" || this.grabHandle === "TR") top = bottom - 8;
      else bottom = top + 8;
    }
    left = Math.max(0, left);
    top = Math.max(0, top);
    right = Math.min(this.bitmapW, right);
    bottom = Math.min(this.bitmapH, bottom);
    const cur = this.rects[this.grabIndex];
    this.rects[this.grabIndex] = { ...cur, rect: { left, top, right, bottom } };
    this.shuffleCache.delete(this.grabIndex);
  }

  private mappedRectView(r: RedactionRect["rect"]): { left: number; top: number; right: number; bottom: number } {
    const tl = this.imageToView({ x: r.left, y: r.top });
    const br = this.imageToView({ x: r.right, y: r.bottom });
    return { left: tl.x, top: tl.y, right: br.x, bottom: br.y };
  }

  private handleAt(p: Point, mapped: { left: number; top: number; right: number; bottom: number }): Handle {
    const pts: Array<[number, number]> = [
      [mapped.left, mapped.top],
      [mapped.right, mapped.top],
      [mapped.left, mapped.bottom],
      [mapped.right, mapped.bottom],
      [(mapped.left + mapped.right) / 2, mapped.top],
      [(mapped.left + mapped.right) / 2, mapped.bottom],
      [mapped.left, (mapped.top + mapped.bottom) / 2],
      [mapped.right, (mapped.top + mapped.bottom) / 2],
    ];
    for (let i = 0; i < pts.length; i++) {
      const [cx, cy] = pts[i];
      if (Math.abs(p.x - cx) <= this.handleSlop && Math.abs(p.y - cy) <= this.handleSlop) {
        return HANDLE_ORDER[i];
      }
    }
    return "NONE";
  }

  private draw(): void {
    const ctx = this.canvas.getContext("2d")!;
    const w = this.canvas.width;
    const h = this.canvas.height;
    ctx.fillStyle = "#ECE6DB";
    ctx.fillRect(0, 0, w, h);

    if (!this.bitmap) return;

    ctx.save();
    ctx.imageSmoothingEnabled = true;
    ctx.setTransform(this.scale, 0, 0, this.scale, this.tx, this.ty);
    ctx.drawImage(this.bitmap as CanvasImageSource, 0, 0);
    ctx.restore();

    // Fill rects (in view space).
    for (let i = 0; i < this.rects.length; i++) {
      const r = this.rects[i];
      const m = this.mappedRectView(r.rect);
      const rw = m.right - m.left;
      const rh = m.bottom - m.top;
      if (r.fillMode === "SHUFFLE") {
        if (this.bitmapImageData) {
          let cached = this.shuffleCache.get(i);
          if (!cached) {
            const t = shuffleTile(this.bitmapImageData, r.rect);
            if (t) {
              cached = t;
              this.shuffleCache.set(i, t);
            }
          }
          if (cached) {
            const tmp = document.createElement("canvas");
            tmp.width = cached.width;
            tmp.height = cached.height;
            tmp.getContext("2d")!.putImageData(cached, 0, 0);
            ctx.globalAlpha = this.previewAlpha / 255;
            ctx.drawImage(tmp, m.left, m.top, rw, rh);
            ctx.globalAlpha = 1;
          }
        }
      } else {
        ctx.fillStyle = `rgba(${red(r.fillColor)}, ${green(r.fillColor)}, ${blue(r.fillColor)}, ${this.previewAlpha / 255})`;
        ctx.fillRect(m.left, m.top, rw, rh);
      }
    }

    // Strokes.
    for (let i = 0; i < this.rects.length; i++) {
      const r = this.rects[i];
      const m = this.mappedRectView(r.rect);
      const rw = m.right - m.left;
      const rh = m.bottom - m.top;
      if (i === this.selectedIndex) {
        ctx.strokeStyle = "rgba(255,220,60,1)";
        ctx.lineWidth = 3;
      } else if (r.source === "AUTO") {
        ctx.strokeStyle = "rgba(60,180,255,1)";
        ctx.lineWidth = 2;
      } else {
        ctx.strokeStyle = "rgba(255,120,60,1)";
        ctx.lineWidth = 2;
      }
      ctx.strokeRect(m.left, m.top, rw, rh);
    }

    // OCR debug overlay — every recognised token boxed in green, with
    // the token text drawn above when there's room. Sits above the
    // redaction strokes so the user can see what was OCR'd vs what the
    // privacy filter chose to cover.
    if (this.debugEnabled && this.debugTokens.length > 0) {
      ctx.save();
      ctx.lineWidth = 1;
      ctx.strokeStyle = "rgba(60,200,120,0.95)";
      ctx.fillStyle = "rgba(60,200,120,0.18)";
      const fontPx = Math.max(9, Math.min(14, Math.round(11 * Math.max(0.6, this.scale))));
      ctx.font = `${fontPx}px ui-monospace, SFMono-Regular, Menlo, monospace`;
      ctx.textBaseline = "bottom";
      for (const tok of this.debugTokens) {
        const m = this.mappedRectView(tok.rect);
        const rw = m.right - m.left;
        const rh = m.bottom - m.top;
        if (rw < 1 || rh < 1) continue;
        ctx.fillRect(m.left, m.top, rw, rh);
        ctx.strokeRect(m.left + 0.5, m.top + 0.5, rw, rh);
        if (rw >= 24) {
          const label = this.debugText.slice(tok.start, tok.endExclusive);
          if (label.trim()) {
            const ty = m.top - 2;
            const tw = ctx.measureText(label).width;
            ctx.fillStyle = "rgba(8,28,18,0.85)";
            ctx.fillRect(m.left, ty - fontPx, Math.min(tw + 6, rw + 40), fontPx + 2);
            ctx.fillStyle = "rgba(180,255,210,1)";
            ctx.fillText(label, m.left + 3, ty);
            ctx.fillStyle = "rgba(60,200,120,0.18)";
          }
        }
      }
      ctx.restore();
    }

    // Rubber-band preview during DRAW_NEW.
    if (this.gesture === "DRAW_NEW" && this.dragStart && this.dragCur) {
      const s = this.dragStart;
      const c = this.dragCur;
      const x = Math.min(s.x, c.x);
      const y = Math.min(s.y, c.y);
      const rw = Math.abs(c.x - s.x);
      const rh = Math.abs(c.y - s.y);
      ctx.strokeStyle = "rgba(255,120,60,1)";
      ctx.setLineDash([6, 4]);
      ctx.lineWidth = 2;
      ctx.strokeRect(x, y, rw, rh);
      ctx.setLineDash([]);
    }

    // Selection handles.
    if (this.hasSelection()) {
      const m = this.mappedRectView(this.rects[this.selectedIndex].rect);
      const sz = 8;
      const pts: Array<[number, number]> = [
        [m.left, m.top], [m.right, m.top], [m.left, m.bottom], [m.right, m.bottom],
        [(m.left + m.right) / 2, m.top], [(m.left + m.right) / 2, m.bottom],
        [m.left, (m.top + m.bottom) / 2], [m.right, (m.top + m.bottom) / 2],
      ];
      ctx.fillStyle = "rgba(255,220,60,1)";
      ctx.strokeStyle = "rgba(40,40,40,1)";
      ctx.lineWidth = 1;
      for (const [cx, cy] of pts) {
        ctx.fillRect(cx - sz, cy - sz, sz * 2, sz * 2);
        ctx.strokeRect(cx - sz, cy - sz, sz * 2, sz * 2);
      }
    }
  }
}
